// src/components/Resources/ResourceFilters.js
import React, { useRef, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SearchIcon, FilterX, Wrench, BookOpen, Users, Flag, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslations } from '../../translations/translations';

const MultiSelectDropdown = ({ value, onValueChange, options, placeholder, icon, translations }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Agregar el listener cuando el dropdown está abierto
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleToggleOption = (option) => {
    const newValue = value.includes(option)
      ? value.filter(v => v !== option)
      : [...value, option];
    onValueChange(newValue);
  };
  
  const handleRemoveOption = (option, e) => {
    e.stopPropagation();
    const newValue = value.filter(v => v !== option);
    onValueChange(newValue);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="flex h-10 w-full items-center justify-between border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
        style={{ backgroundColor: '#f8f8f8' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center flex-1">
          {icon}
          <div className="flex flex-wrap gap-1 flex-1">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : value.length <= 1 ? (
              value.map((item) => (
                <Badge
                  key={item}
                  variant="secondary"
                  className="text-xs px-2 py-0 h-5 flex items-center gap-1"
                >
                  {item}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => handleRemoveOption(item, e)}
                  />
                </Badge>
              ))
            ) : (
              <Badge variant="secondary" className="text-xs px-2 py-0 h-5">
                {value.length} {translations.selected}
              </Badge>
            )}
          </div>
        </div>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-input shadow-lg max-h-60 overflow-auto">
          <div className="p-2">
            <div
              className="flex items-center space-x-2 p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => onValueChange([])}
            >
              <input
                type="checkbox"
                checked={value.length === 0}
                readOnly
                className="rounded border-gray-300"
              />
              <span className="text-sm">{translations.clearSelection}</span>
            </div>
            {options.map((option) => (
              <div
                key={option}
                className="flex items-center space-x-2 p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleToggleOption(option)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(option)}
                  readOnly
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{option}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ResourceFilters = ({ searchTerm, setSearchTerm, filters, setFilters, resourcesData }) => {
  const t = useTranslations();
  
  const resetFilters = () => {
    setFilters({
      tipo: [],
      tema: [],
      organizacion: [],
      pais: []
    });
    setSearchTerm("");
  };

  const areFiltersActive = () => {
    return filters.tipo.length > 0 || 
           filters.tema.length > 0 || 
           filters.organizacion.length > 0 || 
           filters.pais.length > 0 || 
           searchTerm !== "";
  };

  // Obtener opciones únicas para campos multiselect y ordenarlas alfabéticamente
  const getUniqueOptions = (fieldName) => {
    const allOptions = [];
    resourcesData.forEach(resource => {
      if (Array.isArray(resource[fieldName])) {
        allOptions.push(...resource[fieldName]);
      } else if (resource[fieldName]) {
        allOptions.push(resource[fieldName]);
      }
    });
    return [...new Set(allOptions)].sort((a, b) => a.localeCompare(b));
  };

  return (
    <div className="space-y-4 w-full">
      {/* Primera fila: Barra de búsqueda + Botón limpiar filtros */}
      <div className="flex flex-col sm:flex-row gap-4 w-full items-stretch sm:items-center">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            type="text"
            placeholder={t.filters.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
            style={{ backgroundColor: '#f8f8f8' }}
          />
        </div>
        
        <Button
          variant="primary" 
          onClick={resetFilters}
          disabled={!areFiltersActive()}
          className="flex items-center justify-center uppercase text-xs text-white bg-white text-accent border-solid border border-accent hover:bg-accent hover:border-accent hover:text-white whitespace-nowrap px-6 w-full sm:w-auto"
        >
          <FilterX className="h-4 w-4 mr-2" />
          <span>{t.filters.clearFilters}</span>
        </Button>
      </div>

      {/* Segunda fila: Solo los filtros select */}
      <div className="flex flex-col sm:flex-row gap-4 w-full">
        {/* Multiselect para Tipo */}
        <div className="flex-1">
          <MultiSelectDropdown
            value={filters.tipo}
            onValueChange={(value) => setFilters(prev => ({ ...prev, tipo: value }))}
            options={getUniqueOptions('tipo')}
            placeholder={t.filters.type}
            icon={<Wrench className="h-4 w-4 mr-2" />}
            translations={t.filters}
          />
        </div>

        {/* Multiselect para Tema */}
        <div className="flex-1">
          <MultiSelectDropdown
            value={filters.tema}
            onValueChange={(value) => setFilters(prev => ({ ...prev, tema: value }))}
            options={getUniqueOptions('tema')}
            placeholder={t.filters.theme}
            icon={<BookOpen className="h-4 w-4 mr-2" />}
            translations={t.filters}
          />
        </div>

        {/* Multiselect para Organización */}
        <div className="flex-1">
          <MultiSelectDropdown
            value={filters.organizacion}
            onValueChange={(value) => setFilters(prev => ({ ...prev, organizacion: value }))}
            options={getUniqueOptions('organizacion')}
            placeholder={t.filters.organization}
            icon={<Users className="h-4 w-4 mr-2" />}
            translations={t.filters}
          />
        </div>

        {/* Multiselect para País */}
        <div className="flex-1">
          <MultiSelectDropdown
            value={filters.pais}
            onValueChange={(value) => setFilters(prev => ({ ...prev, pais: value }))}
            options={getUniqueOptions('pais')}
            placeholder={t.filters.country}
            icon={<Flag className="h-4 w-4 mr-2" />}
            translations={t.filters}
          />
        </div>
      </div>
    </div>
  );
}

export default ResourceFilters;